import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import useLoading from "../useLoading";
import useAlerts from "../useAlerts";
import useApiErrorHandler from "../useApiErrorHandler";

import {
  setContingencyEventList,
  clearContingencyEventList,
  setContingencyEventDetail,
  clearContingencyEventDetail,
  setContingencyEventLoading,
  setContingencyEventPageSize,
} from "../../store/slices/dte/contingencyEventSlice";
import ContingencyEventService from "../../services/hacienda/ContingencyEventService";

// ✅ useContingencyEvents.ts
export const useContingencyEvents = () => {
  const dispatch: AppDispatch = useDispatch();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show: showAlert } = useAlerts();
  const { handle } = useApiErrorHandler();
  const [detailError, setDetailError] = useState<string | null>(null);

  const {
    list,
    paginator,
    pageSize,
    detail,
    detailLoaded,
    loading,
  } = useSelector((state: RootState) => state.contingencyEvent);

  const handlerGetFilteredEvents = async (
    filters: any,
    page = 0,
    size = pageSize,
    sort = "startTimestamp,desc"
  ) => {
    try {
      dispatch(setContingencyEventLoading(true));
      showLoadingIndicator();

      const response = await ContingencyEventService.getFiltered(filters, page, size, sort);
      if (response?.data?.content !== undefined) {
        dispatch(setContingencyEventList(response.data));
      } else {
        showAlert("error", "Error", "No se pudo obtener la lista de contingencias.");
      }
    } catch (err) {
      handle(err);
    } finally {
      hideLoadingIndicator();
      dispatch(setContingencyEventLoading(false));
    }
  };

  /** ✅ Abrir modal (cargar detalle) */
  const openDetail = async (id: number) => {
    try {
      setDetailError(null);
      dispatch(setContingencyEventLoading(true));
      showLoadingIndicator();

      const response = await ContingencyEventService.getById(id);

      if (response?.data) {
        dispatch(setContingencyEventDetail(response.data));
      } else {
        showAlert("error", "Error", "No se pudo obtener el detalle del evento.");
      }
    } catch (err) {
      setDetailError("No se pudo cargar el detalle. Puede volver a intentarlo desde el historial.");
      handle(err);
    } finally {
      hideLoadingIndicator();
      dispatch(setContingencyEventLoading(false));
    }
  };

  const refreshDetail = useCallback(async (id: number) => {
    try {
      const response = await ContingencyEventService.getById(id);
      if (!response?.data) {
        setDetailError("No se pudo actualizar el proceso. Se conserva la última información disponible.");
        return false;
      }
      dispatch(setContingencyEventDetail(response.data));
      setDetailError(null);
      return true;
    } catch {
      setDetailError("No se pudo actualizar el proceso. Se conserva la última información disponible y se intentará nuevamente.");
      return false;
    }
  }, [dispatch]);

  /** ❌ Cerrar modal */
  const closeDetail = () => {
    setDetailError(null);
    dispatch(clearContingencyEventDetail());
  };

  const clearList = () => dispatch(clearContingencyEventList());

  const updatePageSize = (size: number) => {
    dispatch(setContingencyEventPageSize(size));
  };

  return {
    list,
    paginator,
    pageSize,
    detail,
    detailLoaded,
    loading,

    handlerGetFilteredEvents,
    openDetail,
    refreshDetail,
    detailError,
    closeDetail,
    clearList,
    updatePageSize,
  };
};
